import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import StatusBadge from '../components/ui/StatusBadge';

const OrdersPage = () => {
  const { t } = useLanguage();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [query, setQuery] = useState('');
  const [grid, setGrid] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const params = new URLSearchParams({ page: String(page), limit: '5', ...(statusFilter ? { status: statusFilter } : {}), ...(query ? { search: query } : {}) });
        const res = await fetch(`/api/orders?${params.toString()}`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (!res.ok) throw new Error('Failed to fetch orders');
        const data = await res.json();
        setOrders(
          (data.orders || []).map(o => ({
            ...o,
            preview: (o.items || [])[0]?.image || (o.items || [])[0]?.product_image || null
          }))
        );
        setPages(data.pagination?.total_pages || 1);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isAuthenticated, navigate, page, statusFilter, query]);

  const formatPrice = (value) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(value || 0));

  return (
    <div className="min-h-screen bg-gray-900 py-12 mt-16">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold text-white mb-2">{t('common.orders')}</h1>
          <p className="text-gray-300">Vos commandes</p>
        </div>

        {/* Filters */}
        <div className="mb-4 flex items-center gap-3">
          <select value={statusFilter} onChange={(e)=>{setPage(1);setStatusFilter(e.target.value);}} className="bg-gray-800 text-white border border-gray-700 rounded px-3 py-2">
            <option value="">Tous les statuts</option>
            <option value="pending">En attente</option>
            <option value="confirmed">Confirmée</option>
            <option value="processing">En traitement</option>
            <option value="shipped">Expédiée</option>
            <option value="delivered">Livrée</option>
            <option value="cancelled">Annulée</option>
          </select>
          <input value={query} onChange={(e)=>{setPage(1);setQuery(e.target.value);}} placeholder="Rechercher par #commande" className="bg-gray-800 text-white border border-gray-700 rounded px-3 py-2 flex-1" />
          <button onClick={()=>setGrid(g=>!g)} className="px-3 py-2 rounded bg-gray-700 text-white">{grid ? 'Liste' : 'Grille'}</button>
        </div>

        {loading ? (
          <div className={grid ? 'grid grid-cols-1 md:grid-cols-2 gap-4' : 'space-y-4'}>
            {[...Array(5)].map((_,i)=> (
              <div key={i} className="bg-gray-800 rounded-lg p-5 border border-gray-700 animate-pulse">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-lg bg-gray-700" />
                    <div>
                      <div className="h-4 w-40 bg-gray-700 rounded mb-2" />
                      <div className="h-3 w-28 bg-gray-700 rounded" />
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="h-6 w-20 bg-gray-700 rounded mb-2" />
                    <div className="h-3 w-24 bg-gray-700 rounded mb-2" />
                    <div className="h-4 w-20 bg-gray-700 rounded" />
                  </div>
                </div>
                <div className="mt-4 h-8 w-36 bg-gray-700 rounded" />
              </div>
            ))}
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center text-gray-300">Aucune commande.</div>
        ) : (
          <>
          <div className={grid ? 'grid grid-cols-1 md:grid-cols-2 gap-4' : 'space-y-4'}>
            {orders.map(order => (
              <div key={order.id} className="bg-gray-800 rounded-lg p-5 border border-gray-700">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {order.preview ? (
                      <img src={order.preview} alt="" className="w-16 h-16 object-cover rounded-lg" onError={(e)=>{e.target.style.display='none';}} />
                    ) : (
                      <div className="w-16 h-16 rounded-lg bg-gray-700" />
                    )}
                    <div className="text-white font-medium">#{order.order_number}</div>
                    <div className="text-sm text-gray-400">{new Date(order.created_at).toLocaleString('fr-FR')}</div>
                  </div>
                  <div className="text-right">
                    <div className="mb-2">
                      <StatusBadge status={order.status} size="xsmall" />
                    </div>
                    <div className="text-sm text-gray-300 mb-1">Paiement: <span className="text-white">{order.payment_status}</span></div>
                    <div className="text-yellow-400 font-semibold text-lg">{formatPrice(order.total_amount)}</div>
                  </div>
                </div>
                <div className="mt-4 text-right">
                  <Link to={`/orders/${order.id}`} className="inline-block px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg">Voir la commande</Link>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 flex items-center justify-between">
            <button disabled={page<=1} onClick={()=>setPage(p=>Math.max(1,p-1))} className="px-3 py-2 rounded bg-gray-700 text-white disabled:opacity-50">Précédent</button>
            <div className="text-gray-400">Page {page} / {pages}</div>
            <button disabled={page>=pages} onClick={()=>setPage(p=>Math.min(pages,p+1))} className="px-3 py-2 rounded bg-gray-700 text-white disabled:opacity-50">Suivant</button>
          </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OrdersPage;